import type { CodeTurnStatus } from '@/generated/prisma/enums';
import type { CodeSessionChangedFile } from '@/lib/code-sessions/types';
import type { WorkspaceChange } from '@/lib/code-sessions/workspace-types';
import type {
  OpenCodeEvent,
  SessionLogItem,
  SessionMessageIdle,
  SessionMessageInfo,
} from '@opencode/client/promise';

// A one-off read of the sandbox (the drain check, a finished turn's log) takes
// a moment; one that hangs must not hold the turn, and the session's lease, open.
export const SANDBOX_READ_TIMEOUT_MS = 15_000;

/**
 * Deterministic OpenCode prompt id derived from the cubic turn id —
 * `SessionMessage.ID` requires the `msg_` prefix, and determinism makes
 * admission idempotent across the API path and the turn workflow.
 */
export function deterministicPromptId(turnId: string): string {
  return `msg_${turnId.replace(/-/g, '')}`;
}

/** The OpenCode prompt a turn sent: its admitted inbox item, or the one admission will create. */
export function turnPromptId(turn: { id: string; inboxId: string | null }): string {
  return turn.inboxId ?? deterministicPromptId(turn.id);
}

/**
 * A turn's status while its execution runs; queued counts because OpenCode
 * runs a prompt before cubic records its admission.
 */
export const LIVE_TURN_STATUSES = [
  'queued',
  'admitted',
  'running',
] as const satisfies readonly CodeTurnStatus[];

export type LiveTurnStatus = (typeof LIVE_TURN_STATUSES)[number];

/** Whether a turn whose status is `status` is live (`LIVE_TURN_STATUSES`). */
export function isLiveTurnStatus(status: string): status is LiveTurnStatus {
  return LIVE_TURN_STATUSES.some((live) => live === status);
}

/** A turn's status before OpenCode delivered its prompt into the conversation. */
export const UNDELIVERED_TURN_STATUSES = [
  'queued',
  'admitted',
] as const satisfies readonly CodeTurnStatus[];

export type UndeliveredTurnStatus = (typeof UNDELIVERED_TURN_STATUSES)[number];

/** Whether a turn whose status is `status` is undelivered (`UNDELIVERED_TURN_STATUSES`). */
export function isUndeliveredTurnStatus(status: string): status is UndeliveredTurnStatus {
  return UNDELIVERED_TURN_STATUSES.some((undelivered) => undelivered === status);
}

/** The turn statuses of a run the turn engine works on now. */
export const WORKING_TURN_STATUSES = [
  'admitted',
  'running',
] as const satisfies readonly CodeTurnStatus[];

export type WorkingTurnStatus = (typeof WORKING_TURN_STATUSES)[number];

/** Whether a turn whose status is `status` is working (`WORKING_TURN_STATUSES`). */
export function isWorkingTurnStatus(status: string): status is WorkingTurnStatus {
  return WORKING_TURN_STATUSES.some((working) => working === status);
}

/** A turn's status once it ended. */
export const TERMINAL_TURN_STATUSES = [
  'done',
  'failed',
  'interrupted',
] as const satisfies readonly CodeTurnStatus[];

export type TerminalTurnStatus = (typeof TERMINAL_TURN_STATUSES)[number];

/** Whether a turn whose status is `status` has ended (`TERMINAL_TURN_STATUSES`). */
export function isTerminalTurnStatus(status: string): status is TerminalTurnStatus {
  return TERMINAL_TURN_STATUSES.some((terminal) => terminal === status);
}

/**
 * A turn's status with OpenCode's saved prompts taken into account. A saved
 * prompt proves delivery, so an undelivered turn whose prompt OpenCode saved
 * is running, whatever its row still says.
 */
export function deliveredTurnStatus(
  turn: { id: string; inboxId: string | null; status: string },
  savedPromptIds: Pick<ReadonlySet<string>, 'has'>,
): string {
  return isUndeliveredTurnStatus(turn.status) && savedPromptIds.has(turnPromptId(turn))
    ? 'running'
    : turn.status;
}

/**
 * Starting durable-log cursor for a turn. The server's `after` is a
 * non-negative seq, so `undefined` (param omitted, replay from 0) is only
 * correct for turn 0; later turns fall back to the newest cursor persisted
 * by earlier turns of the same session.
 */
export function resolveTailCursor(
  turn: { lastSeq: number | null },
  previousTurns: readonly { lastSeq: number | null }[],
): number | undefined {
  if (turn.lastSeq !== null) {
    return turn.lastSeq;
  }
  let max = -1;
  for (const previous of previousTurns) {
    if (previous.lastSeq !== null && previous.lastSeq > max) {
      max = previous.lastSeq;
    }
  }
  return max >= 0 ? max : undefined;
}

const EXECUTION_TERMINAL_TYPES = new Set([
  'session.execution.succeeded',
  'session.execution.failed',
  'session.execution.interrupted',
]);

/** Whether an OpenCode event type ends an execution, durable or live. */
export function isExecutionEnd(type: string): boolean {
  return EXECUTION_TERMINAL_TYPES.has(type);
}

/** The session an event belongs to; `form.created` names it inside the form it announces. */
export function sessionIdOf(item: SessionLogItem | OpenCodeEvent): string | undefined {
  if (item.type === 'log.synced') {
    return item.aggregateID;
  }
  if (item.type === 'form.created') {
    return item.data.form.sessionID;
  }
  return 'sessionID' in item.data && typeof item.data.sessionID === 'string'
    ? item.data.sessionID
    : undefined;
}

/**
 * A turn drain is complete once a terminal `session.execution.*` event for this
 * session was observed and the inbox is empty. A non-empty inbox means a queued
 * follow-up is still part of the same drain.
 */
export function isTurnDrainComplete(input: {
  logItems: readonly SessionLogItem[];
  sessionId: string;
  inboxCount: number;
}): boolean {
  if (input.inboxCount > 0) {
    return false;
  }
  return input.logItems.some(
    (item) =>
      item.type !== 'log.synced' &&
      isExecutionEnd(item.type) &&
      item.data.sessionID === input.sessionId,
  );
}

/** The tokens a turn's model calls used, cache reads and writes included. */
export type CodeTurnUsage = {
  input: number;
  output: number;
  reasoning: number;
  cacheRead: number;
  cacheWrite: number;
};

const IDLE_OUTCOME_STATUS = {
  succeeded: 'done',
  failed: 'failed',
  interrupted: 'interrupted',
} as const satisfies Record<SessionMessageIdle['outcome'], string>;

/**
 * The saved messages of the busy period a prompt was delivered into: from the
 * prompt to the idle marker OpenCode saves when the session stops working. A
 * steer or a queued follow-up keeps the period open. The marker is missing
 * while the session works, and after a shutdown, which the session resumes.
 * A `/compact` turn's prompt is the compaction OpenCode saved under its ID.
 */
export function promptBusyPeriod(messages: readonly SessionMessageInfo[], promptId: string) {
  const start = messages.findIndex(
    (message) =>
      (message.type === 'user' || message.type === 'compaction') && message.id === promptId,
  );
  if (start === -1) {
    return undefined;
  }
  const rest = messages.slice(start + 1);
  const idle = rest.find((message): message is SessionMessageIdle => message.type === 'idle');
  return { messages: idle === undefined ? rest : rest.slice(0, rest.indexOf(idle)), idle };
}

/**
 * Candidate prompts of the turns a run ends: its turn's own `promptId`, and
 * each delivered one whose turn the drain ended with it (`drained`). When
 * the observation missed its own delivery, its ID is prepended only as a
 * candidate; saved message history, not this array, determines their order.
 */
export function drainPrompts(input: {
  promptId: string;
  deliveredInboxIds: readonly string[];
  drained: readonly { inboxId: string }[];
}): string[] {
  const { promptId, deliveredInboxIds } = input;
  const drained = new Set(input.drained.map(({ inboxId }) => inboxId));
  const delivered = deliveredInboxIds.includes(promptId)
    ? deliveredInboxIds
    : [promptId, ...deliveredInboxIds];
  return delivered.filter((inboxId) => inboxId === promptId || drained.has(inboxId));
}

/**
 * A drain's saved messages, the prompts of the turns it ended among them, in
 * order, and the idle marker that ended it, missing while it goes on.
 */
export type SavedDrain = {
  promptIds: string[];
  messages: SessionMessageInfo[];
  idle: SessionMessageIdle | undefined;
};

/**
 * The saved messages of the drain that delivered `promptIds`, the prompts of
 * the turns it ended: from the first of them to the idle marker after the
 * last, which OpenCode saved when the drain ended. A steer's interrupt saves an
 * idle marker inside the drain, which then goes on with the steer.
 */
export function savedDrain(
  messages: readonly SessionMessageInfo[],
  promptIds: readonly string[],
): SavedDrain {
  const delivered = new Set(promptIds);
  const prompts = messages.flatMap((message, index) =>
    (message.type === 'user' || message.type === 'compaction') && delivered.has(message.id)
      ? [{ index, id: message.id }]
      : [],
  );
  const first = prompts[0];
  const last = prompts.at(-1);
  if (first === undefined || last === undefined) {
    return { promptIds: [], messages: [], idle: undefined };
  }
  // The last prompt's busy period ends the drain.
  const period = promptBusyPeriod(messages, last.id);
  return {
    promptIds: prompts.map(({ id }) => id),
    messages: [...messages.slice(first.index, last.index + 1), ...(period?.messages ?? [])],
    idle: period?.idle,
  };
}

/** How a drain ended; undefined until it has. */
export function savedTurnOutcome(
  drain: SavedDrain,
): (typeof IDLE_OUTCOME_STATUS)[SessionMessageIdle['outcome']] | undefined {
  return drain.idle === undefined ? undefined : IDLE_OUTCOME_STATUS[drain.idle.outcome];
}

/**
 * The tokens the model calls of a drain used: its steps, failed ones included,
 * and its compactions. Subagents run in child sessions and are not counted, nor
 * is title generation, which OpenCode keeps off the messages.
 */
export function savedTurnUsage(drain: SavedDrain): CodeTurnUsage | undefined {
  const counted = drain.messages.flatMap((message) =>
    'tokens' in message && message.tokens !== undefined ? [message.tokens] : [],
  );
  if (counted.length === 0) {
    return undefined;
  }
  return counted.reduce<CodeTurnUsage>(
    (usage, tokens) => ({
      input: usage.input + tokens.input,
      output: usage.output + tokens.output,
      reasoning: usage.reasoning + tokens.reasoning,
      cacheRead: usage.cacheRead + tokens.cache.read,
      cacheWrite: usage.cacheWrite + tokens.cache.write,
    }),
    { input: 0, output: 0, reasoning: 0, cacheRead: 0, cacheWrite: 0 },
  );
}

/** A completed tool call in a turn's saved messages, and the prompt it answered. */
export type SavedToolCall = { callId: string; input: unknown; promptId: string };

/**
 * The completed calls to any of `tools` in a drain, in order, each with the
 * prompt before it, whose author asked for it: each steer or follow-up the
 * drain delivered is a prompt of its own. A failed call does not count;
 * OpenCode hands the error to the model, which calls again.
 */
export function savedTurnToolCalls<Tool extends string>(
  drain: SavedDrain,
  ...tools: Tool[]
): (SavedToolCall & { tool: Tool })[] {
  let answered = drain.promptIds[0] ?? '';
  const calls: (SavedToolCall & { tool: Tool })[] = [];
  for (const message of drain.messages) {
    if (message.type === 'user') {
      answered = message.id;
    } else if (message.type === 'assistant') {
      for (const content of message.content) {
        if (
          content.type === 'tool' &&
          tools.includes(content.name as Tool) &&
          content.state.status === 'completed'
        ) {
          const tool = content.name as Tool;
          calls.push({ callId: content.id, input: content.state.input, promptId: answered, tool });
        }
      }
    }
  }
  return calls;
}

/**
 * The text of a turn's last reply that has any, and the error its last failed
 * step saved. The reply runs from the first step after its prompt to the next
 * prompt, within the prompt's busy period: steers OpenCode delivers together
 * share the reply after them, and what follows the next prompt belongs to that
 * prompt's turn.
 */
export function savedTurnFinalText(messages: readonly SessionMessageInfo[], promptId: string) {
  const period = promptBusyPeriod(messages, promptId)?.messages ?? [];
  const first = period.findIndex((message) => message.type === 'assistant');
  const reply = first === -1 ? [] : period.slice(first);
  const next = reply.findIndex((message) => message.type === 'user');
  const replies = (next === -1 ? reply : reply.slice(0, next)).flatMap((message) =>
    message.type === 'assistant' ? [message] : [],
  );
  const texts = replies.map((reply) =>
    reply.content.flatMap((part) => (part.type === 'text' ? [part.text] : [])).join(''),
  );
  return {
    text: texts.findLast((text) => text.trim() !== '') ?? '',
    error: replies.findLast((reply) => reply.error !== undefined)?.error?.message,
  };
}

/**
 * The replies of a drain: by prompt, the text of each one's last reply that has
 * any, which its turn saves (`finalText`), and the drain's last text and
 * error, which a parent hears as the agent's final answer.
 */
export type DrainReplies = {
  texts: Record<string, string>;
  last: { text: string; error: string | undefined };
};

/** `drain`'s replies. Its last is the latest reply with text, in drain order. */
export function drainReplies(drain: SavedDrain): DrainReplies {
  const replies = drain.promptIds.map((id) => ({ id, ...savedTurnFinalText(drain.messages, id) }));
  return {
    texts: Object.fromEntries(replies.flatMap(({ id, text }) => (text === '' ? [] : [[id, text]]))),
    last: {
      text: replies.findLast(({ text }) => text !== '')?.text ?? '',
      error: replies.findLast(({ error }) => error !== undefined)?.error,
    },
  };
}

/** The last of `savedTurnToolCalls`: the call that stands when the model called again. */
export function savedTurnToolCall(drain: SavedDrain, name: string): SavedToolCall | undefined {
  return savedTurnToolCalls(drain, name).at(-1);
}

/**
 * A turn's changed files: the working tree's changes since the session's start
 * commit, exactly as the changes endpoint lists them, untracked files included (as
 * added). Totals the reader could not count, such as a binary or oversized
 * file's, stay unknown rather than zero.
 */
export function turnChangedFiles(files: readonly WorkspaceChange[]): CodeSessionChangedFile[] {
  return files.map((file) => {
    const counted = !file.binary && !file.tooLarge && !file.totalsIncomplete;
    return {
      path: file.path,
      additions: counted ? file.additions : null,
      deletions: counted ? file.deletions : null,
      status: file.status,
    };
  });
}
